import { Controller } from "@hotwired/stimulus"

// Wires a DDT field's picker modal back to its plain text inputs.
//
// The icon opens a modal (generic ModalController) whose Turbo Frame is server-rendered by the
// register's own index action (Recipient/DeliveryLocation, picker: true) — search, select, and
// create-inline all live there. This controller only reacts to a selection: it never filters or
// fetches anything itself.
//
// Turbo Frames are ordinary DOM nodes, not real iframes, so a click on a row rendered inside the
// frame bubbles normally up to this controller, as long as this controller's element is an
// ancestor of the frame (it wraps icon + modal + frame + fields for one logical field).
export default class extends Controller {
  static targets = ["nameField", "addressField", "idField", "frame"]

  // Bound to a row rendered inside the frame: data-action="click->entity-picker#select"
  // data-id/-name/-address on the row.
  select(event) {
    const { id, name, address } = event.currentTarget.dataset
    this.apply(id, name, address)
    this.closeModal()
  }

  // The create-inline response re-renders the frame with the new record's row already marked
  // data-entity-picker-autoselect="true" — no inline <script>, just a standard Turbo event.
  frameLoaded() {
    const autoSelect = this.frameTarget.querySelector("[data-entity-picker-autoselect='true']")
    if (autoSelect) {
      autoSelect.click()
      return
    }

    // A live-search reload replaces the search input with a fresh DOM node, dropping focus mid-
    // typing. Restore it (and the cursor position) so the next keystroke doesn't need a re-click.
    const searchField = this.frameTarget.querySelector("input[type='search']")
    if (searchField) {
      searchField.focus()
      const end = searchField.value.length
      searchField.setSelectionRange(end, end)
    }
  }

  // Live search: bound to input->entity-picker#search on the frame's search field. Debounced so
  // it doesn't fire a request per keystroke; requestSubmit (not .submit()) so HTML5 validation and
  // normal event dispatch still happen, keeping this a plain Turbo Frame-scoped GET underneath.
  search(event) {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => event.target.form.requestSubmit(), 300)
  }

  apply(id, name, address) {
    this.idFieldTarget.value = id || ""
    this.nameFieldTarget.value = name || ""
    if (this.hasAddressFieldTarget) this.addressFieldTarget.value = address || ""
  }

  closeModal() {
    const modalController = this.application.getControllerForElementAndIdentifier(
      this.element.querySelector("[data-controller~='modal']"),
      "modal"
    )
    if (modalController) modalController.close()
  }
}
